import React, { useCallback, useRef, useState } from "react";
import PropTypes from "prop-types";

import AnchoredPopover from "./AnchoredPopover";
import { PlusIcon } from "../labInvestigations/labIcons";
import { fromDateKey, todayDate } from "./data";
import { Chevron } from "./scheduleIcons";
import { FILLED_DAYS_YEARS } from "./useFilledDays";

/**
 * "+ Add/Edit schedule" — opens a month calendar to pick the day to build
 * or edit. Four tile states (mock: "September 2026"):
 *
 *   • selected     — the day the section is currently showing: solid purple
 *   • filled       — a day that has a schedule: lavender tile, purple digits
 *   • not filled   — a past day with no schedule: plain, still clickable
 *   • future       — disabled (routines are reports of days that happened)
 *
 * Which days are filled comes from the section's shared five-year fetch
 * (`filled` is that Set), so opening the calendar costs no request; the
 * month range is capped at those five years. Picking a day hands its key
 * up — the section moves to it: a filled day opens in view mode (pencil
 * to edit, like today), an empty day straight into the builder.
 */

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_LETTERS = ["S", "M", "T", "W", "T", "F", "S"];

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);
const monthOf = (key) => (key || "").slice(0, 7); // "YYYY-MM"
const shiftMonth = (ym, n) => {
  const [y, m] = ym.split("-").map((x) => parseInt(x, 10));
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
};
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map((x) => parseInt(x, 10));
  return `${MONTH_NAMES[m - 1]} ${y}`;
};
// Leading blanks for the weekday the month starts on, then one key per day.
const monthCells = (ym) => {
  const [y, m] = ym.split("-").map((x) => parseInt(x, 10));
  const first = new Date(y, m - 1, 1);
  const days = new Date(y, m, 0).getDate();
  const cells = [];
  for (let i = 0; i < first.getDay(); i += 1) cells.push(null);
  for (let d = 1; d <= days; d += 1) cells.push(`${ym}-${pad2(d)}`);
  return cells;
};

const ScheduleCalendar = ({ date, filled, onPick, disabled, busy }) => {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => monthOf(date));
  const wrapRef = useRef(null);
  const today = todayDate();
  const thisMonth = monthOf(today);
  const firstMonth = shiftMonth(thisMonth, -12 * FILLED_DAYS_YEARS);

  // Opening always starts on the selected day's month.
  const toggle = () => {
    setOpen((o) => {
      if (!o) setMonth(monthOf(date));
      return !o;
    });
  };

  const close = useCallback(() => setOpen(false), []);

  const pick = (key) => {
    setOpen(false);
    if (!key || key > today) return;
    onPick(key);
  };

  return (
    <div className="ds6-cal-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`ds6-head-btn${open ? " is-open" : ""}`}
        onClick={toggle}
        disabled={disabled || busy}
        title={disabled ? "Already editing this day's schedule" : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <PlusIcon /> Add/Edit schedule
      </button>

      <AnchoredPopover
        open={open}
        anchorRef={wrapRef}
        onClose={close}
        className="ds6-cal"
        role="dialog"
        aria-label="Choose a day to add or edit"
      >
          <div className="ds6-cal-head">
            <button
              type="button"
              className="ds6-cal-nav"
              onClick={() => setMonth((m) => shiftMonth(m, -1))}
              disabled={month <= firstMonth}
              aria-label="Previous month"
            >
              <Chevron dir="left" />
            </button>
            <span className="ds6-cal-title">{monthLabel(month)}</span>
            <button
              type="button"
              className="ds6-cal-nav"
              onClick={() => setMonth((m) => shiftMonth(m, 1))}
              disabled={month >= thisMonth}
              aria-label="Next month"
            >
              <Chevron dir="right" />
            </button>
          </div>

          <div className="ds6-cal-grid" role="grid">
            {WEEKDAY_LETTERS.map((w, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span className="ds6-cal-dow" role="columnheader" key={`dow-${i}`}>
                {w}
              </span>
            ))}
            {monthCells(month).map((key, i) => {
              if (!key) {
                // eslint-disable-next-line react/no-array-index-key
                return <span className="ds6-cal-blank" key={`blank-${i}`} aria-hidden="true" />;
              }
              const future = key > today;
              const selected = key === date;
              const has = filled.has(key);
              const cls = [
                "ds6-cal-day",
                has ? "has-schedule" : "",
                selected ? "is-selected" : "",
              ].filter(Boolean).join(" ");
              const dt = fromDateKey(key);
              const label = dt ? dt.getDate() : "";
              return (
                <button
                  type="button"
                  className={cls}
                  key={key}
                  role="gridcell"
                  disabled={future}
                  aria-current={selected ? "date" : undefined}
                  aria-label={`${label}${has ? ", has a schedule" : ""}${selected ? ", selected" : ""}`}
                  title={future ? undefined : has ? "Open this day's schedule" : "Add a schedule for this day"}
                  onClick={() => pick(key)}
                >
                  {label}
                </button>
              );
            })}
          </div>
      </AnchoredPopover>
    </div>
  );
};

ScheduleCalendar.propTypes = {
  date: PropTypes.string.isRequired, // YYYY-MM-DD — the day the section shows
  filled: PropTypes.instanceOf(Set).isRequired,
  onPick: PropTypes.func.isRequired,
  disabled: PropTypes.bool, // builder already open
  busy: PropTypes.bool, // template loading
};

export default ScheduleCalendar;
